/**
 * Sheet para compartilhar o arquivo PDF (WhatsApp, e-mail, etc.) ou baixar no aparelho.
 * No web/PWA substitui o visualizador do navegador, que força «salvar cópia» / Google Drive.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { Card } from '@/shared/ui/Card'
import { Button } from '@/shared/ui/Button'
import { CloseIcon, DownloadIcon, FileTextIcon, ShareIcon } from '@/shared/ui/icons'
import { useDialogA11y } from '@/shared/ui/useDialogA11y'
import { useToast } from '@/shared/ui/toast'
import { usePdfShareStore } from '@/shared/lib/pdfShareStore'
import {
  canSharePdfFile,
  downloadPdfFile,
  pdfBytesToFile,
  sharePdfFile,
} from '@/shared/lib/platform/files'
import { toFriendlyErrorMessage } from '@/shared/lib/errorMessage'
import {
  loadClinicalDocumentShareText,
  shareOrMintPublicDocumentLink,
} from '@/shared/lib/shareClinicalDocumentLink'

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

export const PdfShareSheet = () => {
  const pending = usePdfShareStore((s) => s.pending)
  const dismiss = usePdfShareStore((s) => s.dismiss)
  const toast = useToast()
  const open = pending !== null

  const titleId = useId()
  const descriptionId = useId()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const primaryRef = useRef<HTMLButtonElement | null>(null)
  const [busy, setBusy] = useState<'share' | 'download' | 'link' | null>(null)
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (!open) setBusy(null)
  }

  const file = useMemo(() => {
    if (!pending) return null
    return pdfBytesToFile(pending.bytes, pending.filename)
  }, [pending])

  const canShare = file ? canSharePdfFile(file) : false

  useDialogA11y(open, dismiss, primaryRef, { historyBack: true })

  useEffect(() => {
    if (!open) return
    const panel = panelRef.current
    if (!panel) return

    const handleTabKey = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return
      const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      )
      if (focusables.length === 0) return
      const first = focusables[0]
      const last = focusables[focusables.length - 1]
      const active = document.activeElement
      if (event.shiftKey) {
        if (active === first || !panel.contains(active)) {
          event.preventDefault()
          last.focus()
        }
        return
      }
      if (active === last || !panel.contains(active)) {
        event.preventDefault()
        first.focus()
      }
    }

    panel.addEventListener('keydown', handleTabKey)
    return () => panel.removeEventListener('keydown', handleTabKey)
  }, [open])

  if (!pending || !file) return null

  const handleShare = async () => {
    setBusy('share')
    try {
      downloadPdfFile(pending.bytes, pending.filename)
      let text = pending.title
      if (pending.documentId) {
        try {
          text = await loadClinicalDocumentShareText(pending.documentId, pending.title)
        } catch {
          /* segue com o arquivo mesmo se o link público não puder ser montado agora */
        }
      }
      const result = await sharePdfFile(file, pending.title, text)
      if (result === 'shared') {
        dismiss()
        return
      }
      if (result === 'unavailable') {
        toast.error('Compartilhamento de arquivo não disponível neste navegador. Baixe o PDF.')
      }
    } catch (err) {
      toast.error(toFriendlyErrorMessage(err, 'Não foi possível compartilhar o PDF.'))
    } finally {
      setBusy(null)
    }
  }

  const handleDownload = () => {
    setBusy('download')
    try {
      downloadPdfFile(pending.bytes, pending.filename)
      dismiss()
    } catch (err) {
      toast.error(toFriendlyErrorMessage(err, 'Não foi possível baixar o PDF.'))
      setBusy(null)
    }
  }

  const handleShareLink = async () => {
    if (!pending.documentId) return
    setBusy('link')
    try {
      const mode = await shareOrMintPublicDocumentLink(pending.documentId, pending.title)
      if (mode === 'copied') toast.success('Link copiado para a área de transferência.')
      dismiss()
    } catch (err) {
      toast.error(toFriendlyErrorMessage(err, 'Não foi possível compartilhar o link.'))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div
      className="fixed inset-0 z-[130] flex items-end justify-center bg-black/50 p-4 sm:items-center"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) dismiss()
      }}
    >
      <div ref={panelRef} className="w-full max-w-[440px] overscroll-contain">
        <Card
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          className="space-y-4 rounded-[var(--radius)] p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-[0_18px_45px_rgba(31,43,40,0.22)]"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--color-mint-soft)]">
                <FileTextIcon size={22} className="text-[var(--color-teal-ink)]" aria-hidden="true" />
              </span>
              <div className="min-w-0 space-y-1">
                <h2 id={titleId} className="text-base font-bold text-[var(--color-text-primary)]">
                  PDF pronto
                </h2>
                <p className="truncate text-xs text-[var(--color-text-muted)]" title={pending.filename}>
                  {pending.filename}
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Fechar"
              disabled={Boolean(busy)}
              onClick={dismiss}
            >
              <CloseIcon size={18} aria-hidden="true" />
            </Button>
          </div>

          <div id={descriptionId} className="space-y-2 text-sm text-[var(--color-text-secondary)]">
            {canShare ? (
              <p>
                Compartilhe o PDF, o link público e o código de acesso pelo WhatsApp, e-mail ou outro app, ou baixe para este aparelho.
              </p>
            ) : (
              <p>
                Neste navegador o compartilhamento direto do arquivo não está disponível. Baixe o PDF
                {pending.documentId ? ', envie o link público' : ''} ou envie o arquivo pelo WhatsApp ou e-mail.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            {canShare ? (
              <Button
                ref={primaryRef}
                type="button"
                variant="primary"
                fullWidth
                disabled={Boolean(busy)}
                onClick={() => void handleShare()}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <ShareIcon size={18} aria-hidden="true" />
                  {busy === 'share' ? 'Abrindo…' : 'Compartilhar arquivo'}
                </span>
              </Button>
            ) : null}
            <Button
              ref={canShare ? undefined : primaryRef}
              type="button"
              variant={canShare ? 'secondary' : 'primary'}
              fullWidth
              disabled={Boolean(busy)}
              onClick={handleDownload}
            >
              <span className="inline-flex items-center justify-center gap-2">
                <DownloadIcon size={18} aria-hidden="true" />
                {busy === 'download' ? 'Baixando…' : 'Baixar para o aparelho'}
              </span>
            </Button>
            {pending.documentId ? (
              <Button
                type="button"
                variant="secondary"
                fullWidth
                disabled={Boolean(busy)}
                onClick={() => void handleShareLink()}
              >
                <span className="inline-flex items-center justify-center gap-2">
                  <ShareIcon size={18} aria-hidden="true" />
                  {busy === 'link' ? 'Abrindo…' : 'Compartilhar link'}
                </span>
              </Button>
            ) : null}
            <Button type="button" variant="ghost" fullWidth disabled={Boolean(busy)} onClick={dismiss}>
              Agora não
            </Button>
          </div>
        </Card>
      </div>
    </div>
  )
}